import { useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useDialogLayer } from "./Dialog";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";

/**
 * A thing opened over the whole screen — the sidebar and the header included — the way a request opens from the
 * Requests list: a header with what it is and a close, then its body. A page, not a dialog: no card around it and its
 * own pinned bar, but it is a layer like Dialog (useDialogLayer: Escape, focus kept inside and handed back). `fit`
 * hands the body to a page that lays itself out (its own scroll, a pinned bar); otherwise the body scrolls as one.
 */
export function Overlay({
  onClose,
  leading,
  title,
  trailing,
  fit = false,
  children,
  footer,
}: {
  onClose: () => void;
  /** Before the title: an agent's picture, a mark. */
  leading?: ReactNode;
  title: ReactNode;
  /** Between the title and the close: a filter, a switch. */
  trailing?: ReactNode;
  fit?: boolean;
  children: ReactNode;
  /** A bar pinned under the body: a decision bar. */
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useDialogLayer(true, onClose, panel);

  return createPortal(
    <div ref={panel} tabIndex={-1} className="animate-fade-up fixed inset-0 z-50 flex flex-col bg-surface outline-none" role="dialog" aria-modal="true">
      <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-3 md:px-6">
        {leading}
        <div className="min-w-0 flex-1">{title}</div>
        {trailing}
        <IconButton label="Close" onClick={onClose}>
          <Icon name="close" className="size-4" />
        </IconButton>
      </div>
      {fit ? <div className="flex min-h-0 flex-1 flex-col">{children}</div> : <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">{children}</div>}
      {footer && (
        <div className="shrink-0 border-t border-line bg-surface/90 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur md:px-6">{footer}</div>
      )}
    </div>,
    document.body,
  );
}
